import { Action, Reducer } from 'redux';
import {
  ACTION, ClearBowtieData, RequestBowtie, ReceiveBowtie, FailBowtie,
  RequestDraft, ReceiveDraft, FailDraft,
  RequestConnectDraft, ReceiveConnectDraft, FailConnectDraft,
  RequestCase, ReceiveCase, FailCase,
  RequestConnectedCases, ReceiveConnectedCases, FailConnectedCases,
  CleanData,
  RequestBowtieFavourite,
  ReceiveBowtieFavourite,
  IncrementRequestId,
  ReceiveConnectedCasesByConnectionType,
  UpdateBowtie,
  RequestChangeConnectionSequence,
  ReceiveChangeConnectionSequence,
  FailChangeConnectionSequence,
  FailValidateCaseId,
  RequestValidateCaseId,
  ReceiveValidateCaseId,
  AddInstance,
  ClearInstance
} from './action-types';
import { BowtieInstanceState, BowtieStoreState } from './types-store';
import { produce } from 'immer';
import { EXECUTE_STATUS } from 'app/area/reports/containers/chart/types-store';

export const INITIAL_BOWTIE_INSTANCE_STATE: BowtieInstanceState = {
  currentCaseNumber: undefined,
  currentDraftId: undefined,
  executeStatus: EXECUTE_STATUS.IDLE,
  caseDataModel: undefined,
  bowtieLayout: undefined,
  caseTemplate: undefined,
  fetchingDraft: false,
  draftRequestId: 0,
  draftData: undefined,
  draftEtag: undefined,
  fetchingConnections: [],
  casesRequiringUpdate: [],
  connectionTypes: undefined,
  inspectionEvaluations: {
    fetching: [],
    evaluationStatuses: {}
  },
  connectedCases: {
    fetching: {},
    connected: {}
  },
  creatingBowtieFavourite: false,
  bowtieFavouriteId: undefined,
  requestId: 0,
  connectedCasesByConnectionType: undefined,
  changeConnectionSequenceId: undefined
};

export const INITIAL_BOWTIE_STATE: BowtieStoreState = {
  instances: {},
  texts: {
    id: 0,
    texts: []
  },
  textsLanguage: null,
  textsFetching: false
};

const updateStateForCaseIds = (
  instance: BowtieInstanceState | undefined,
  caseNumber: number,
  entityId: number,
  fetching: boolean
) => {
  if (instance) {
    instance.stateForCaseIds = {
      ...(instance.stateForCaseIds || {}),
      [caseNumber]: { valid: !!entityId, fetching }
    };
  }
};

const reducers: { [indexer: string]: Reducer<BowtieStoreState, Action> } = {

  [ACTION.CLEAR_BOWTIE_DATA]: (state: BowtieStoreState, action: ClearBowtieData): BowtieStoreState => {
    return produce(state, draft => {
      draft.instances[action.instanceKey] = {
          ...INITIAL_BOWTIE_INSTANCE_STATE,
          bowtieFavouriteId: state.instances[action.instanceKey]?.bowtieFavouriteId,
          requestId: state.instances[action.instanceKey]?.requestId || 0
      };
      return draft;
    });
  },

  [ACTION.REQUEST_BOWTIE]: (state: BowtieStoreState, action: RequestBowtie): BowtieStoreState => {
    return produce(state, draft => {
      draft.instances[action.instanceKey] = {
        ...draft.instances[action.instanceKey],
        currentCaseNumber: action.caseNumber,
        currentDraftId: action.draftId,
        executeStatus: EXECUTE_STATUS.PENDING
      };
      return draft;
    });
  },

  [ACTION.RECEIVE_BOWTIE]: (state: BowtieStoreState, action: ReceiveBowtie): BowtieStoreState => {
    const currentInstanceState = state.instances?.[action.instanceKey];
    if (currentInstanceState.currentCaseNumber !== action.caseNumber ||
      currentInstanceState.currentDraftId !== action.draftId) {
      return state;
    }
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.executeStatus = EXECUTE_STATUS.SUCCEEDED;
        instance.caseDataModel = action.caseDataModel;
        instance.bowtieLayout = action.bowtieLayout;
        instance.caseTemplate = action.caseTemplate;
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          evaluationStatuses: {
            ...instance.inspectionEvaluations?.evaluationStatuses,
            ...action.evaluations
          }
        };
      }
    });
  },

  [ACTION.FAIL_BOWTIE]: (state: BowtieStoreState, action: FailBowtie): BowtieStoreState => {
    const currentInstanceState = state.instances?.[action.instanceKey];
    if (currentInstanceState.currentCaseNumber !== action.caseNumber ||
      currentInstanceState.currentDraftId !== action.draftId) {
      return state;
    }
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.executeStatus = EXECUTE_STATUS.FAILED;
        instance.caseDataModel = undefined;
        instance.caseTemplate = undefined;
      }
    });
  },

  [ACTION.REQUEST_DRAFT]: (state: BowtieStoreState, action: RequestDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingDraft = true;
        instance.draftRequestId = action.requestId;
      }
    });
  },

  [ACTION.RECEIVE_DRAFT]: (state: BowtieStoreState, action: ReceiveDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance?.draftRequestId === action.requestId) {
        instance.fetchingDraft = false;
        instance.draftData = action.draftData;
        instance.connectionTypes = action.connectionTypes;
        instance.draftEtag = action.draftEtag;
        instance.casesRequiringUpdate = instance.casesRequiringUpdate
          .filter(caseNumber => instance.fetchingConnections.includes(caseNumber));
      }
    });
  },

  [ACTION.FAIL_DRAFT]: (state: BowtieStoreState, action: FailDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance?.draftRequestId === action.requestId) {
        instance.fetchingDraft = false;
        instance.casesRequiringUpdate = instance.casesRequiringUpdate
          .filter(caseNumber => instance.fetchingConnections.includes(caseNumber));
      }
    });
  },

  [ACTION.REQUEST_CASE]: (state: BowtieStoreState, action: RequestCase): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          fetching: [
            ...instance.inspectionEvaluations.fetching,
            action.caseNumber
          ]
        };
      }
    });
  },

  [ACTION.RECEIVE_CASE]: (state: BowtieStoreState, action: ReceiveCase): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          fetching: instance.inspectionEvaluations.fetching.filter(caseNumber => caseNumber !== action.caseNumber),
          evaluationStatuses: {
            ...instance.inspectionEvaluations.evaluationStatuses,
            [action.caseNumber]: action.evaluation
          }
        };
      }
    });
  },

  [ACTION.FAIL_CASE]: (state: BowtieStoreState, action: FailCase): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.inspectionEvaluations = {
          ...instance.inspectionEvaluations,
          fetching: instance.inspectionEvaluations.fetching.filter(caseNumber => caseNumber !== action.caseNumber)
        };
      }
    });
  },

  [ACTION.REQUEST_CONNECTED_CASES]: (state: BowtieStoreState, action: RequestConnectedCases): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCases = {
          ...instance.connectedCases,
          fetching: {
            [action.caseNumber]: [
              ...(instance.connectedCases.fetching?.[action.caseNumber] ?? []),
              action.connectionType
            ]
          }
        };
      }
    });
  },

  [ACTION.RECEIVE_CONNECTED_CASES]: (state: BowtieStoreState, action: ReceiveConnectedCases): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCases = {
          ...instance.connectedCases,
          fetching: {
            [action.caseNumber]: (instance.connectedCases.fetching?.[action.caseNumber] ?? [])
              .filter(connectionType => connectionType !== action.connectionType)
          },
          connected: {
            ...instance.connectedCases.connected,
            [action.caseNumber]: {
              ...(instance.connectedCases.connected?.[action.caseNumber] ?? {}),
              [action.connectionType]: action.connected
            }
          }
        };
      }
    });
  },

  [ACTION.FAIL_CONNECTED_CASES]: (state: BowtieStoreState, action: FailConnectedCases): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCases = {
          ...instance.connectedCases,
          fetching: {
            [action.caseNumber]: (instance.connectedCases.fetching?.[action.caseNumber] ?? [])
              .filter(connectionType => connectionType !== action.connectionType)
          }
        };
      }
    });
  },

  [ACTION.CLEAN_DATA]: (state: BowtieStoreState, action: CleanData): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        Object.assign(instance, {
          ...INITIAL_BOWTIE_INSTANCE_STATE,
          requestId: instance.requestId || 0,
          bowtieLayout: instance.bowtieLayout,
          draftRequestId: instance.draftRequestId || 0,
          bowtieFavouriteId: undefined
        });
      }
    });
  },

  [ACTION.REQUEST_CONNECT_DRAFT]: (state: BowtieStoreState, action: RequestConnectDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        if (!instance.fetchingConnections.some(c => c === action.caseNumber)) {
          instance.fetchingConnections.push(action.caseNumber);
          instance.casesRequiringUpdate.push(action.caseNumber);
        }
      }
    });
  },

  [ACTION.RECEIVE_CONNECT_DRAFT]: (state: BowtieStoreState, action: ReceiveConnectDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingConnections = instance.fetchingConnections.filter(c => c !== action.caseNumber);
      }
    });
  },

  [ACTION.FAIL_CONNECT_DRAFT]: (state: BowtieStoreState, action: FailConnectDraft): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.fetchingConnections = instance.fetchingConnections.filter(c => c !== action.caseNumber);
      }
    });
  },

  [ACTION.REQUEST_BOWTIE_FAVOURITE]: (state: BowtieStoreState, action: RequestBowtieFavourite): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.creatingBowtieFavourite = true;
        instance.bowtieFavouriteId = undefined;
      }
    });
  },

  [ACTION.RECEIVE_BOWTIE_FAVOURITE]: (state: BowtieStoreState, action: ReceiveBowtieFavourite): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.creatingBowtieFavourite = false;
        instance.bowtieFavouriteId = action.bowtieFavouriteId;
      }
    });
  },

  [ACTION.INCREMENT_REQUEST_ID]: (state: BowtieStoreState, action: IncrementRequestId): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.requestId = (instance?.requestId ?? 0) + 1;
      }
    });
  },

  [ACTION.RECEIVE_CONNECTED_CASES_BY_CONNECTION_TYPE]: (state: BowtieStoreState, action: ReceiveConnectedCasesByConnectionType):
    BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.connectedCasesByConnectionType = {
          ...instance.connectedCasesByConnectionType,
          [action.caseNumber]: {
            ...action.connectedCases
          }
        };
      }
    });
  },

  [ACTION.UPDATE_BOWTIE]: (state: BowtieStoreState, action: UpdateBowtie): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.caseDataModel = action.caseDataModel;
        instance.caseTemplate = action.caseTemplate;
         if (action.connectedCases !== undefined) {
           instance.connectedCases = {
             ...instance.connectedCases,
             connected: {
               ...instance.connectedCases?.connected,
               [action.caseNumber]: {
                 ...action.connectedCases
               }
             }
           };
         }
      }
    });
  },

  [ACTION.REQUEST_CHANGE_CONNECTION_SEQUENCE]: (state: BowtieStoreState, action: RequestChangeConnectionSequence): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.changeConnectionSequenceId = action.caseNumber;
      }
    });
  },

  [ACTION.RECEIVE_CHANGE_CONNECTION_SEQUENCE]: (state: BowtieStoreState, action: ReceiveChangeConnectionSequence): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.changeConnectionSequenceId = undefined;
      }
    });
  },

  [ACTION.FAIL_CHANGE_CONNECTION_SEQUENCE]: (state: BowtieStoreState, action: FailChangeConnectionSequence): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      if (instance) {
        instance.changeConnectionSequenceId = undefined;
      }
    });
  },

  [ACTION.REQUEST_VALIDATE_CASE_ID]: (state: BowtieStoreState, action: RequestValidateCaseId): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      updateStateForCaseIds(instance, action.caseNumber, undefined, true);
    });
  },

  [ACTION.RECEIVE_VALIDATE_CASE_ID]: (state: BowtieStoreState, action: ReceiveValidateCaseId): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      updateStateForCaseIds(instance, action.caseNumber, action.entityId, false);
    });
  },

  [ACTION.FAIL_VALIDATE_CASE_ID]: (state: BowtieStoreState, action: FailValidateCaseId): BowtieStoreState => {
    return produce(state, draft => {
      const instance = draft.instances[action.instanceKey];
      updateStateForCaseIds(instance, action.caseNumber, undefined, true);
    });
  },

  [ACTION.ADD_INSTANCE]: (state: BowtieStoreState, action: AddInstance): BowtieStoreState => {
    if (state.instances?.[action.instanceKey]) {
      return state;
    }
    return {
      ...state,
      instances: {
        ...state.instances,
        [action.instanceKey]: {
          ...INITIAL_BOWTIE_INSTANCE_STATE
        }
      }
    };
  },

  [ACTION.CLEAR_INSTANCE]: (state: BowtieStoreState, action: ClearInstance): BowtieStoreState => {
    if (!state.instances?.[action.instanceKey]) {
      return state;
    }
    return {
      ...state,
      instances: Object.keys(state.instances).reduce(
        (aggr, key) => {
          if (key !== action.instanceKey) {
            aggr[key] = state.instances[key];
          }
          return aggr;
        }, {}
      )
    };
  }
};


export const reducer: Reducer<BowtieStoreState> = (state: BowtieStoreState = INITIAL_BOWTIE_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
